import { Injectable } from "@angular/core";
import {
  Dataset,
  EventType,
  Job,
  JobInput,
  JobState,
  MetadataFile,
  Resource,
  Rule,
  Session,
  WsEvent,
} from "chipster-js-common";
import { FileState } from "chipster-js-common/lib/model/dataset";
import { clone } from "lodash-es";
import log from "loglevel";
import { Observable, forkJoin, from as observableFrom, lastValueFrom, merge as observableMerge, of } from "rxjs";
import { catchError, concatMap, filter, mergeMap } from "rxjs/operators";
import { TokenService } from "../../../core/authentication/token.service";
import { ErrorService } from "../../../core/errorhandler/error.service";
import { RestErrorService } from "../../../core/errorhandler/rest-error.service";
import { SessionData } from "../../../model/session/session-data";
import { FileResource } from "../../../shared/resources/fileresource";
import { SessionResource } from "../../../shared/resources/session.resource";
import { TabService } from "../../../shared/services/tab.service";
import { ToastMessage, ToastService } from "../../../shared/services/toast.service";
import UtilsService from "../../../shared/utilities/utils";
import { DialogModalService } from "./dialogmodal/dialogmodal.service";
import { SelectionHandlerService } from "./selection-handler.service";
import { SessionEventService } from "./session-event.service";
import { SessionService } from "./session.service";

@Injectable()
export class SessionDataService {
  private sessionId: string;

  constructor(
    private sessionResource: SessionResource,
    private sessionService: SessionService,
    private fileResource: FileResource,
    private errorService: ErrorService,
    private tokenService: TokenService,
    private sessionEventService: SessionEventService,
    private selectionHandlerService: SelectionHandlerService,
    private toastService: ToastService,
    private restErrorService: RestErrorService,
    private dialogModalService: DialogModalService,
    private tabService: TabService,
  ) {}

  getSessionId(): string {
    return this.sessionId;
  }

  setSessionId(id: string) {
    this.sessionId = id;
  }

  createDataset(dataset: Dataset): Observable<string> {
    return this.sessionResource.createDataset(this.getSessionId(), dataset);
  }

  getDataset(datasetId: string): Observable<Dataset> {
    return this.sessionResource.getDataset(this.getSessionId(), datasetId);
  }

  createJob(job: Job) {
    return this.sessionResource.createJob(this.getSessionId(), job);
  }

  getJobById(jobId: string, jobs: Map<string, Job>) {
    return jobs.get(jobId);
  }

  createJobs(jobs: Job[]) {
    return this.sessionResource.createJobs(this.getSessionId(), jobs);
  }

  /**
   * Create a dataset which is derived from some other datasets.
   *
   * The file content is uploaded to the server and a fake job is created, so
   * that the datasets' relationships are shown correctly in the workflowgraph graph.
   *
   * @param name Name of the new dataset
   * @param sourceDatasetIds Array of datasetIds shown as inputs for the new dataset
   * @param toolName e.g. name of the visualization that created this dataset
   * @param content File content, the actual data
   * @returns Observable providing datasetId of the created dataset
   */
  createDerivedDataset(
    name: string,
    sourceDatasets: Dataset[],
    toolName: string,
    content: string,
    toolCategory = "Interactive visualizations",
    metadataFiles: MetadataFile[] = null,
  ): Observable<string> {
    const job = new Job();
    job.state = JobState.Completed;
    // FIXME don't hardcode category
    job.toolCategory = toolCategory;
    job.toolName = toolName;

    let inputCount = 0;
    job.inputs = sourceDatasets.map((dataset) => {
      const input = new JobInput();
      input.datasetId = dataset.datasetId;
      input.inputId = "input" + inputCount;
      input.displayName = "Input " + inputCount;
      input.datasetName = dataset.name;
      inputCount += 1;
      return input;
    });

    return this.createJob(job).pipe(
      mergeMap((jobId: string) => {
        const d = new Dataset(name);
        d.sourceJob = jobId;
        if (metadataFiles != null) {
          d.metadataFiles = metadataFiles;
        }
        return this.createDataset(d);
      }),
      mergeMap((datasetId: string) =>
        forkJoin([of(datasetId), this.fileResource.uploadData(this.getSessionId(), datasetId, content)]),
      ),
      mergeMap((result) => of(result[0])),
      catchError((err) => {
        log.info("create derived dataset failed", err);
        throw err;
      }),
    );
  }

  cancelJob(job: Job) {
    return lastValueFrom(this.sessionResource.cancelJob(this.sessionId, job));
  }

  deleteJobs(jobs: Job[]) {
    const deleteJobs$ = jobs.map((job: Job) => this.sessionResource.deleteJob(this.getSessionId(), job.jobId));
    observableMerge(...deleteJobs$).subscribe({
      next: () => {
        log.info("Job deleted");
      },
      error: (err) => this.restErrorService.showError("delete jobs failed", err),
    });
  }

  deleteDatasets(datasets: Dataset[], sessionId: string) {
    const deleteDatasets$ = datasets.map((dataset: Dataset) =>
      this.sessionResource.deleteDataset(sessionId, dataset.datasetId),
    );
    observableMerge(...deleteDatasets$).subscribe({
      next: () => {
        log.info("Dataset deleted");
      },
      error: (err) => this.restErrorService.showError("delete datasets failed", err),
    });
  }

  updateDataset(dataset: Dataset) {
    return this.sessionResource.updateDataset(this.sessionId, dataset);
  }

  updateDatasets(datasets: Dataset[]) {
    return this.sessionResource.updateDatasets(this.sessionId, datasets);
  }

  updateJob(job: Job) {
    return lastValueFrom(this.sessionResource.updateJob(this.getSessionId(), job));
  }

  getDatasetUrl(dataset: Dataset): Observable<string> {
    return this.sessionService.getDatasetUrl(this.getSessionId(), dataset);
  }

  exportDatasets(datasets: Dataset[]) {
    this.sessionService.exportDatasets(this.getSessionId(), datasets);
  }

  openNewTab(dataset: Dataset) {
    this.tabService.openNewTab(this.getSessionId(), dataset);
  }

  hasReadWriteAccess(sessionData: SessionData) {
    const rules = this.getApplicableRules(sessionData.session.rules);
    return rules.some((rule) => rule.readWrite);
  }

  hasPersonalRule(rules: Array<Rule>) {
    return this.getPersonalRules(rules).length > 0;
  }

  getPersonalRules(rules: Array<Rule>) {
    return rules.filter((r) => r.username === this.tokenService.getUsername());
  }

  getPublicRules(rules: Array<Rule>) {
    return rules.filter((r) => r.username === "everyone");
  }

  getApplicableRules(rules: Array<Rule>) {
    return this.getPersonalRules(rules).concat(this.getPublicRules(rules));
  }

  deletePersonalRules(session: Session) {
    return observableFrom(this.getPersonalRules(session.rules)).pipe(
      concatMap((rule: Rule) => this.sessionResource.deleteRule(session.sessionId, rule.ruleId)),
    );
  }

  /**
   * Get pending shares for the UI
   *
   * A simple array of rules isn't enough, because the session name is
   * useful in the UI. Create a copy of the session for each shared rule
   * (there can be more than one for each session) and add only that particular
   * rule to the session's rule array.
   */
  getPendingShares(sessions: Session[]): Session[] {
    const username = this.tokenService.getUsername();

    const sharedSessions = [];

    sessions.forEach((session) => {
      session.rules.forEach((rule) => {
        if (rule.sharedBy === username) {
          const sharedSession = clone(session);
          sharedSession.rules = [rule];
          sharedSessions.push(sharedSession);
        }
      });
    });

    return sharedSessions;
  }

  isMySession(session: Session): boolean {
    return session.rules.some((rule) => rule.username === this.tokenService.getUsername() && !rule.sharedBy);
  }

  /**
   * TODO get exampleSessionOwnerUserId from somewhere else
   * @param session
   * @param exampleSessionOwnerUserId
   */
  isExampleSession(session: Session, exampleSessionOwnerUserId: string): boolean {
    return session.rules.some((rule) => exampleSessionOwnerUserId && rule.sharedBy === exampleSessionOwnerUserId);
  }

  isReadOnlySession(session: Session) {
    return !this.getApplicableRules(session.rules).some((rule) => rule.readWrite);
  }

  // Added the delete dataset code here as two components are sharing the code
  deleteDatasetsNow(deletedDatasets: Dataset[], sessionId: string) {
    // delete from the server
    this.deleteDatasets(deletedDatasets, sessionId);
  }

  deleteDatasetsUndo(deletedDatasets: Dataset[], sessionId: string) {
    // show datasets again in the workflowgraph
    deletedDatasets.forEach((dataset: Dataset) => {
      const wsEvent = new WsEvent(sessionId, Resource.Dataset, dataset.datasetId, EventType.Create, FileState.Complete);
      this.sessionEventService.generateLocalEvent(wsEvent);
    });
  }

  openDeleteFilesConfirm(datasets: Dataset[]) {
    /*
    Remember the session that these datasets are deleted from.

    The confirmation dialog isn't dismissed when the user opens another session, so we have
    to remember the session already here, not when the user clicks the delete button.
    */
    const sessionId = this.getSessionId();

    this.dialogModalService.openDeleteFilesModal(datasets).then(
      () => {
        this.deleteDatasetsLater(datasets, sessionId);
      },
      () => {
        // modal dismissed
      },
    );
  }

  /**
   * Poor man's undo for the dataset deletion.
   *
   * Hide the dataset from the client for fifteen
   * seconds and delete from the server only after that. deleteDatasetsUndo() will
   * cancel the timer, and make the datasets visible again if the user is still in the same
   * session. Session copying and sharing should filter out these hidden datasets or we need
   * a proper server side support for this.
   */
  deleteDatasetsLater(datasets: Dataset[], sessionId: string) {
    log.info("deleting datasets" + datasets);
    // make a copy so that further selection changes won't change the array
    const deletedDatasets = clone(datasets);

    // all selected datasets are going to be deleted
    // clear selection to avoid problems in other parts of the UI
    this.selectionHandlerService.clearDatasetSelection();

    // hide from the workflowgraph
    // state seems to be irrelevant when deleting
    deletedDatasets.forEach((dataset: Dataset) => {
      const wsEvent = new WsEvent(sessionId, Resource.Dataset, dataset.datasetId, EventType.Delete, null);
      this.sessionEventService.generateLocalEvent(wsEvent);
    });

    let msg: ToastMessage;

    if (deletedDatasets.length === 1) {
      msg = [{ text: "Deleted file " }, { text: deletedDatasets[0].name, bold: true }];
    } else {
      msg = "Deleted " + deletedDatasets.length + " files";
    }

    const BTN_UNDO = "Undo";

    const toast = this.toastService.info(msg, "", {
      closeButton: true,
      tapToDismiss: false,
      timeout: 15000,
      progressBar: true,
      buttons: [
        {
          text: BTN_UNDO,
          icon: "fas fa-undo",
          class: "btn-info",
        },
      ],
    });

    let undone = false;

    toast.onAction.pipe(filter((text) => text === BTN_UNDO)).subscribe({
      next: () => {
        undone = true;
        this.deleteDatasetsUndo(deletedDatasets, sessionId);
        this.toastService.close(toast);
      },
      error: (err) => this.errorService.showError("error in dataset deletion", err),
    });

    // closed by the timer or the close button
    toast.afterClosed.subscribe({
      next: () => {
        if (!undone) {
          this.deleteDatasetsNow(deletedDatasets, sessionId);
        }
      },
      error: (err) => this.errorService.showError("error in dataset deletion", err),
    });
  }

  getSessionSize(sessionData: SessionData): number {
    const datasetList = this.getDatasetList(sessionData);
    if (datasetList.length > 0) {
      return this.getDatasetList(sessionData)
        .map((dataset: Dataset) => dataset.size)
        .reduce((total, current) => total + current, 0);
    }
    return 0; // return 0 when no datasets
  }

  /*
    Filter out uploading datasets

    Datasets are created when comp starts to upload them, but there are no type tags until the
    upload is finished. Hide these uploading datasets from the workflow, file list and dataset search.
    When those cannot be selected, those cannot cause problems in the visualization, which assumes that
    the type tags do exist.
    */
  getCompleteDatasets(datasetsMap: Map<string, Dataset>): Map<string, Dataset> {
    // convert to array[[key1, value1], [key2, value2], ...] for filtering and back to map
    return new Map(
      Array.from(datasetsMap).filter((entry) => {
        const dataset = entry[1];
        return dataset.fileId != null;
      }),
    );
  }

  getDatasetList(sessionData: SessionData): Dataset[] {
    return UtilsService.mapValues(this.getCompleteDatasets(sessionData.datasetsMap));
  }

  getJobList(sessionData: SessionData): Job[] {
    return UtilsService.mapValues(sessionData.jobsMap);
  }

  getDatasetListSortedByCreated(sessionData: SessionData): Dataset[] {
    // sort by created date, oldest first (string comparison should do with the current date format)
    return this.getDatasetList(sessionData).sort((a, b) => UtilsService.compareStringNullSafe(a.created, b.created));
  }
}
